import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { portalAuth, portalDb } from '../utils/firebase';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { toast, Toaster } from 'react-hot-toast';
import { doc, updateDoc, getDoc } from 'firebase/firestore';

const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  
  const navigate = useNavigate();

  // Check for email and password parameters in URL when component mounts
  useEffect(() => {
    const emailFromUrl = searchParams.get('email');
    const passwordFromUrl = searchParams.get('password');
    
    console.log("Login parameters detected:", emailFromUrl ? "Email present" : "No email", passwordFromUrl ? "Password present" : "No password");
    
    // Set the email and password in state
    if (emailFromUrl) {
      setEmail(emailFromUrl);
    }
    
    if (passwordFromUrl) {
      setPassword(passwordFromUrl);
    }
    
    // Auto-login only if both email and password are provided
    if (emailFromUrl && passwordFromUrl) {
      console.log("Attempting auto-login...");
      
      // Use a separate effect for auto-login to ensure state is updated first
      setTimeout(async () => {
        try {
          setIsLoading(true);
          console.log("Signing in with:", emailFromUrl);
          
          // Attempt to sign in
          const userCredential = await signInWithEmailAndPassword(portalAuth, emailFromUrl, passwordFromUrl);
          
          // Update last login timestamp
          if (userCredential.user) {
            // Get current saved reports to track for new ones
            try {
              const userDoc = await doc(portalDb, 'users', userCredential.user.uid);
              const userData = (await getDoc(userDoc)).data();
              const savedReports = userData?.savedReports || [];
              
              await updateDoc(userDoc, {
                lastLogin: new Date().toISOString(),
                previousReports: savedReports
              });
            } catch (err) {
              console.error("Error updating last login:", err);
            }
          }
          
          console.log("Auto-login successful!");
          // Ensure body overflow is reset
          document.body.style.overflow = 'auto';
          
          // Clean URL after successful login
          window.history.replaceState({}, document.title, window.location.pathname);
          
          // Redirect to dashboard with minimal delay
          setTimeout(() => {
            navigate('/');
          }, 100);
        } catch (error) {
          console.error("Auto-login failed:", error);
          
          // Show error messages but don't clear credentials
          // so user can try manual login
          if (error.code === 'auth/invalid-credential') {
            toast.error('Invalid email or password');
          } else if (error.code === 'auth/user-not-found') {
            toast.error('No account found with this email.');
          } else if (error.code === 'auth/wrong-password') {
            toast.error('Incorrect password. Please try again.');
          } else if (error.code === 'auth/too-many-requests') {
            toast.error('Too many failed attempts. Please try again later.');
          } else {
            toast.error('Login failed. Please try again.');
          }
          
          setIsLoading(false);
        }
      }, 250); // Reduced delay for faster login while still ensuring state is ready
    } else if (emailFromUrl) {
      // Focus the password field if we only have email
      const passwordInput = document.getElementById('password');
      if (passwordInput) {
        setTimeout(() => passwordInput.focus(), 100);
      }
    }
  }, [searchParams, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter both email and password');
      return;
    }
    
    try {
      setIsLoading(true);
      const userCredential = await signInWithEmailAndPassword(portalAuth, email, password);
      
      // Update last login timestamp
      if (userCredential.user) {
        try {
          const userDoc = await doc(portalDb, 'users', userCredential.user.uid);
          const userData = (await getDoc(userDoc)).data();
          const savedReports = userData?.savedReports || [];
          
          await updateDoc(userDoc, {
            lastLogin: new Date().toISOString(),
            previousReports: savedReports
          });
        } catch (err) {
          console.error("Error updating last login:", err);
        }
      }
      
      toast.success('Logged in successfully!');
      
      // Clear any DOM elements or styles that might persist
      document.body.style.overflow = 'auto';
      
      // Navigate with a slight delay to allow cleanup
      setTimeout(() => {
        navigate('/');
      }, 100);
    } catch (error) {
      console.error(error);
      if (error.code === 'auth/invalid-credential') {
        toast.error('Invalid email or password');
      } else if (error.code === 'auth/user-not-found') {
        toast.error('No account found with this email.');
      } else if (error.code === 'auth/wrong-password') {
        toast.error('Incorrect password. Please try again.');
      } else if (error.code === 'auth/too-many-requests') {
        toast.error('Too many failed attempts. Please try again later.');
      } else {
        toast.error('Login failed: ' + error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e?.preventDefault();
    if (!resetEmail) {
      toast.error('Please enter your email address');
      return;
    }

    setResetLoading(true);
    try {
      await sendPasswordResetEmail(portalAuth, resetEmail);
      toast.success('Password reset email sent! Check your inbox.');
      setShowResetModal(false);
    } catch (error) {
      console.error('Reset password error:', error);
      if (error.code === 'auth/user-not-found') {
        toast.error('No account found with this email.');
      } else {
        toast.error('Failed to send reset email: ' + error.message);
      }
    } finally {
      setResetLoading(false);
    }
  };

  // Reset Password Modal Component
  const ResetPasswordModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Reset Password</h3>
        <form onSubmit={handleForgotPassword}>
          <div className="mb-4">
            <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              id="reset-email"
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your email"
              required
            />
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => {
                setShowResetModal(false);
                setResetEmail('');
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={resetLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {resetLoading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 py-8">
      <Toaster 
        position="top-center" 
        containerStyle={{
          zIndex: 9999999, // Very high z-index to ensure toasts appear above all other elements
          position: "relative",
          pointerEvents: "auto"
        }}
        toastOptions={{
          // Custom styles for the individual toasts
          style: {
            zIndex: 9999999,
            position: "relative"
          }
        }}
      />
      
      <div className="max-w-md w-full mx-4 space-y-6 p-8 bg-white rounded-3xl shadow-xl border border-slate-100">
        <div className="flex flex-col items-center">
          <div className="w-24 h-24 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full flex items-center justify-center mb-4 shadow-lg">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-500 to-cyan-400 bg-clip-text text-transparent">Welcome</h2>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleLogin}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          <button
            type="submit"
            id="login-button"
            disabled={isLoading}
            className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-[#2B7FE8] hover:bg-[#2472d4] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {isLoading ? 'Signing in...' : 'Log In'}
          </button>
          
          <div className="text-center">
            <button
              type="button"
              onClick={() => setShowResetModal(true)}
              className="text-sm text-blue-500 hover:text-blue-600 focus:outline-none focus:underline"
            >
              Forgot your password?
            </button>
          </div>
        </form>
        
        <div className="text-center text-sm text-gray-600">
          <p>
            Don't have an account?{' '}
            <Link to="/register" className="text-blue-500 hover:text-blue-600 hover:underline">
              Create an account
            </Link>
          </p>
        </div>
      </div>
      
      {showResetModal && <ResetPasswordModal />}
    </div>
  );
};

export default LoginPage;